import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X, Clock, RefreshCw, AlertCircle } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

const PRIORITY_STYLES = {
  high: { bg: "rgba(225,72,77,0.12)", color: "#B91C1C", label: "High" },
  medium: { bg: "rgba(20,184,184,0.14)", color: "#0E7B7B", label: "Medium" },
  low: { bg: "rgba(143,143,154,0.12)", color: "#6B6B7A", label: "Low" },
};

function PriorityPill({ priority }) {
  const style = PRIORITY_STYLES[priority] || PRIORITY_STYLES.low;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: style.bg, color: style.color }}
      data-testid={`priority-${priority || "low"}`}
    >
      {style.label}
    </span>
  );
}

export default function NextActionsPanel({ caseId, isReadOnly, onCaseRefreshed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState({});
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState("");

  const fetchData = async (forceRegenerate = false) => {
    setLoading(true);
    try {
      const { data: out } = await api.post(
        `/cases/${caseId}/copilot/next-actions`,
        { force_regenerate: forceRegenerate },
      );
      setData(out);
    } catch (e) {
      toast.error("Could not load Co-pilot suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const apply = async (s) => {
    setWorking((w) => ({ ...w, [s.id]: "applying" }));
    try {
      const action_text = s.action || "";
      const looksLikeFollowUp = /(schedule|follow[-\s]?up|interview|meet|review|update)/i.test(
        action_text,
      );
      const applied_action_type = looksLikeFollowUp ? "set_follow_up" : "logged_only";
      const body = {
        suggestion_id: s.id,
        suggestion_text: action_text,
        rationale: s.rationale || "",
        applied_action_type,
      };
      await api.post(`/cases/${caseId}/copilot/apply-suggestion`, body);
      if (applied_action_type === "set_follow_up") {
        toast.success("Follow-up scheduled and noted on the case");
      } else {
        toast.success("Noted on the case audit log");
      }
      // Remove this suggestion from the local list
      setData((d) =>
        d
          ? { ...d, suggestions: (d.suggestions || []).filter((x) => x.id !== s.id) }
          : d,
      );
      onCaseRefreshed?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Apply failed";
      toast.error(typeof detail === "string" ? detail : "Apply failed");
    } finally {
      setWorking((w) => {
        const n = { ...w };
        delete n[s.id];
        return n;
      });
    }
  };

  const dismiss = async () => {
    const s = dismissTarget;
    if (!s || !dismissReason.trim()) return;
    setWorking((w) => ({ ...w, [s.id]: "dismissing" }));
    try {
      await api.post(`/cases/${caseId}/copilot/dismiss-suggestion`, {
        suggestion_id: s.id,
        suggestion_text: s.action || "",
        reason: dismissReason.trim(),
      });
      toast.success("Suggestion dismissed");
      setData((d) =>
        d
          ? { ...d, suggestions: (d.suggestions || []).filter((x) => x.id !== s.id) }
          : d,
      );
      setDismissTarget(null);
      setDismissReason("");
    } catch (e) {
      toast.error("Dismiss failed");
    } finally {
      setWorking((w) => {
        const n = { ...w };
        delete n[s?.id];
        return n;
      });
    }
  };

  return (
    <div className="brand-card flex flex-col" data-testid="next-actions-panel">
      <div className="flex items-center justify-between border-b border-[color:var(--border-subtle)] px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-[color:var(--page-fg)]">
            Suggested next actions
          </h3>
          <p className="text-[11px] text-[color:var(--page-fg-muted)]">
            {data?.source === "rule_based"
              ? "Rule-based fallback (AI unavailable)"
              : "AI-generated, refreshed every 30 min"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData(true)}
          disabled={loading || isReadOnly}
          data-testid="next-actions-regenerate"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Regenerate
        </Button>
      </div>

      <div className="flex-1 space-y-3 px-5 py-4">
        {!data && loading && (
          <p className="py-6 text-center text-sm text-[color:var(--page-fg-muted)]">Generating suggestions…</p>
        )}
        {data && !data.llm_available && data.source === "rule_based" && (
          <div
            className="flex items-start gap-2 rounded-lg border border-brand-gold/30 bg-brand-gold/10 px-3 py-2 text-xs text-[color:var(--page-fg)]"
            data-testid="ai-unavailable-banner"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 text-brand-gold" />
            <span>
              AI unavailable — showing rule-based suggestions. Try Regenerate later.
            </span>
          </div>
        )}
        {data && (data.suggestions || []).length === 0 && (
          <p
            className="py-6 text-center text-sm text-[color:var(--page-fg-muted)]"
            data-testid="next-actions-empty"
          >
            No suggestions right now. The case looks well-handled.
          </p>
        )}
        {data &&
          (data.suggestions || []).map((s) => (
            <div
              key={s.id}
              className="rounded-xl border border-[color:var(--border-subtle)] bg-white p-4 transition-colors hover:border-brand-teal/50"
              data-testid={`suggestion-card-${s.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-[color:var(--page-fg)]">
                      {s.action}
                    </p>
                    <PriorityPill priority={s.priority} />
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-[color:var(--page-fg-muted)]">
                    {s.rationale}
                  </p>
                  {(s.evidence || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.evidence.map((e, i) => (
                        <span
                          key={i}
                          className="rounded bg-[color:var(--surface-2)] px-1.5 py-0.5 text-[10px] font-mono text-[color:var(--page-fg-secondary)]"
                          data-testid={`suggestion-evidence-${s.id}-${i}`}
                        >
                          {e}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDismissTarget(s)}
                  disabled={isReadOnly || !!working[s.id]}
                  data-testid={`suggestion-dismiss-${s.id}`}
                >
                  <X className="mr-1 h-3.5 w-3.5" />
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  onClick={() => apply(s)}
                  disabled={isReadOnly || !!working[s.id]}
                  data-testid={`suggestion-apply-${s.id}`}
                  className="bg-brand-teal text-white hover:bg-brand-tealDeep"
                >
                  {working[s.id] === "applying" ? (
                    <Clock className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                  )}
                  Apply
                </Button>
              </div>
            </div>
          ))}
      </div>

      <Dialog
        open={!!dismissTarget}
        onOpenChange={(v) => {
          if (!v) {
            setDismissTarget(null);
            setDismissReason("");
          }
        }}
      >
        <DialogContent data-testid="dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Dismiss suggestion</DialogTitle>
            <DialogDescription>
              Tell us briefly why this suggestion is not applicable. The reason
              will be audit-logged for accountability.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason</Label>
            <Textarea
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              maxLength={400}
              rows={4}
              placeholder="e.g. Already addressed during the kickoff meeting."
              data-testid="dismiss-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDismissTarget(null);
                setDismissReason("");
              }}
              data-testid="dismiss-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={dismiss}
              disabled={!dismissReason.trim()}
              data-testid="dismiss-confirm"
              className="bg-brand-teal text-white hover:bg-brand-tealDeep"
            >
              Dismiss with reason
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
